import { ColumnDef } from "@tanstack/react-table";
import { BiDotsHorizontalRounded } from "react-icons/bi";
import { VscRunAll } from "react-icons/vsc";
import { MdOutlineReplay, MdDoNotDisturb } from "react-icons/md";
import { FiUpload } from "react-icons/fi";

// Helper functions
const enumToStringConverter = (value: string) => {
	if (!value) return "";
	return value
		.split("_")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(" ");
};

const moneyFormat = (amount: number) => {
	if (!amount && amount !== 0) return "0.00";
	return amount.toLocaleString("en-US", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
};

const formatDateAndTime = (params: any) => {
	if (!params.value) return "";
	const date = new Date(params.value);
	return date.toLocaleString();
};

const getPayrollRunStatusColor = (status: string) => {
	switch (status) {
		case "COMPLETED":
			return "#4CAF50";
		case "ERROR":
			return "#F44336";
		case "PROCESSING":
			return "#FFC107";
		case "SUBMITTED":
			return "#2196F3";
		default:
			return "#BF21F3";
	}
};

// ==================== COMPANY PAYROLL RUN COLUMNS ====================
export const companyPayrollRunColumns: ColumnDef<any>[] = [
	{
		accessorKey: "payPeriod",
		header: "Pay Period",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => {
			const payPeriod = getValue() as any;
			return payPeriod?.code || "--";
		},
	},
	{
		accessorKey: "initiatedBy",
		header: "Initiated By",
		size: 260,
		minSize: 200,
		maxSize: 400,
		cell: ({ getValue }) => {
			const user = getValue() as any;
			if (!user) return "--";
			return (
				<div className="flex items-center space-x-3">
					<p className="text-sm">Admin</p>
				</div>
			);
		},
	},
	{
		accessorKey: "totalCost",
		header: "Total Cost",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "numberOfEmployees",
		header: "Number Of Employees",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) =>
			new Intl.NumberFormat().format(Number(getValue())) || "--",
	},
	{
		accessorKey: "status",
		header: "Status",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => {
			const status = getValue() as string;
			if (!status) return <p>--</p>;

			return (
				<div
					className="rounded-lg px-4 py-2 min-w-[80px] text-center relative font-semibold overflow-hidden"
					style={{ color: getPayrollRunStatusColor(status) }}
				>
					<div
						className="absolute inset-0"
						style={{
							background: getPayrollRunStatusColor(status),
							opacity: 0.15,
						}}
					/>
					<p>{status === "ERROR" ? "Failed" : enumToStringConverter(status)}</p>
				</div>
			);
		},
	},
	{
		accessorKey: "createdAt",
		header: "Date Created",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => formatDateAndTime({ value: getValue() }),
	},
	{
		id: "actions",
		header: "Actions",
		size: 150,
		minSize: 100,
		maxSize: 200,
		cell: ({ row, table }) => {
			const meta = table.options.meta as any;
			const status = row.original.status as string;
			const isApproved = row.original.isApproved;

			return (
				<div
					className={`flex items-center text-xl ${
						!isApproved && "text-primary-blue"
					}`}
				>
					{/* Run/Re-run Payroll Button */}
					<button
						className="py-2 px-4 rounded-full flex items-center gap-3 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
						onClick={(e) => {
							e.stopPropagation();
							if (meta?.onRunPayroll) {
								meta.onRunPayroll(row.original, e);
							}
						}}
						disabled={isApproved || status === "PROCESSING"}
						title={
							isApproved
								? "Cannot re-run payroll because the payslips have been published"
								: status === "SUBMITTED"
									? "Run Payroll"
									: status === "PROCESSING"
										? "Recent run still processing"
										: "Re-run Payroll"
						}
					>
						{status === "SUBMITTED" ? (
							<VscRunAll />
						) : status === "PROCESSING" ? (
							<MdDoNotDisturb />
						) : (
							<MdOutlineReplay />
						)}
					</button>

					{/* Publish Payslips Button - Only show if completed */}
					{status === "COMPLETED" && (
						<button
							className="py-2 px-4 rounded-full flex items-center gap-3 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
							onClick={(e) => {
								e.stopPropagation();
								if (meta?.onPublishPayslips) {
									meta.onPublishPayslips(row.original, e);
								}
							}}
							disabled={isApproved}
							title={
								isApproved ? "Payslips already published" : "Publish Payslips"
							}
						>
							<FiUpload />
						</button>
					)}
				</div>
			);
		},
	},
];

export const companyPayrollRunColumnVisibility = {
	payPeriod: true,
	initiatedBy: true,
	totalCost: true,
	numberOfEmployees: true,
	status: true,
	createdAt: true,
	actions: true,
};

// ==================== COMPANY PAYSLIPS COLUMNS ====================
export const companyPayslipsColumns: ColumnDef<any>[] = [
	{
		accessorKey: "employee",
		header: "Employee",
		size: 260,
		minSize: 200,
		maxSize: 400,
		cell: ({ getValue }) => {
			const employee = getValue() as any;
			if (!employee) return "--";
			return (
				<div className="flex items-center space-x-3">
					<p className="text-sm truncate">
						{employee?.firstName} {employee?.lastName}
					</p>
				</div>
			);
		},
	},
	{
		accessorKey: "basicPayAmount",
		header: "Basic Pay",
		size: 210,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "grossAmount",
		header: "Gross Pay",
		size: 210,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "netPayAmount",
		header: "Net Pay",
		size: 210,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "employeePensionAmount",
		header: "Employee Pension Contribution",
		size: 250,
		minSize: 200,
		maxSize: 350,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "employerPensionAmount",
		header: "Employer Pension Contribution",
		size: 250,
		minSize: 200,
		maxSize: 350,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "taxAmount",
		header: "Income Tax",
		size: 210,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "employeeSavingSchemeAmount",
		header: "Employee Saving Scheme Amount",
		size: 280,
		minSize: 220,
		maxSize: 400,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "employerSavingSchemeAmount",
		header: "Employer Saving Scheme Amount",
		size: 280,
		minSize: 220,
		maxSize: 400,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "allowanceAmount",
		header: "Allowance Amount",
		size: 210,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "overtimeAmount",
		header: "Overtime Amount",
		size: 210,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "bonusAmount",
		header: "Bonus Amount",
		size: 210,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "backpayAmount",
		header: "Backpay Amount",
		size: 210,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "loanRepaymentAmount",
		header: "Loan Repayment Amount",
		size: 210,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "partialSalaryAmount",
		header: "Partial Salary Amount",
		size: 210,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "salaryAdvanceAmount",
		header: "Salary Advance Amount",
		size: 210,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "unionDeductionsAmount",
		header: "Union Deductions Amount",
		size: 210,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
];

export const companyPayslipsColumnVisibility = {
	employee: true,
	grossAmount: true,
	basicPayAmount: true,
	netPayAmount: true,
	employeePensionAmount: true,
	employerPensionAmount: true,
	taxAmount: true,
	employeeSavingSchemeAmount: false,
	employerSavingSchemeAmount: false,
	allowanceAmount: false,
	overtimeAmount: false,
	bonusAmount: false,
	backpayAmount: false,
	loanRepaymentAmount: false,
	partialSalaryAmount: false,
	salaryAdvanceAmount: false,
	unionDeductionsAmount: false,
	actions: true,
};

// ==================== SALARY REPORT COLUMNS ====================
export const salaryReportColumns: ColumnDef<any>[] = [
	{
		accessorKey: "employeeCode",
		header: "Employee Code",
		size: 200,
		minSize: 150,
		maxSize: 300,
	},
	{
		accessorKey: "employeeName",
		header: "Employee Name",
		size: 220,
		minSize: 180,
		maxSize: 350,
	},
	{
		accessorKey: "basicPayAmount",
		header: "Basic Pay",
		size: 210,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "allowanceAmount",
		header: "Allowance Amount",
		size: 210,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "grossAmount",
		header: "Gross Amount",
		size: 210,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "ssfEmployeeAmount",
		header: "SSF Employee Amount",
		size: 210,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "taxableAmount",
		header: "Taxable Amount",
		size: 210,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "incomeTaxAmount",
		header: "Income Tax Amount",
		size: 210,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "savingSchemesAmount",
		header: "Saving Schemes Amount",
		size: 210,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "otherDeductionsAmount",
		header: "Other Deductions Amount",
		size: 210,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "loansAmount",
		header: "Loan Amount",
		size: 210,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "totalDeductionsAmount",
		header: "Total Deductions Amount",
		size: 210,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "bonusAmount",
		header: "Bonus Amount",
		size: 210,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "primaryPayAmount",
		header: "Primary Pay",
		size: 210,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "secondaryPayAmount",
		header: "Secondary Pay",
		size: 210,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "deductibleReliefAmount",
		header: "Deductible Reliefs",
		size: 210,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "totalReliefAmount",
		header: "Total Reliefs",
		size: 210,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "explicitDeductionAmount",
		header: "Explicit Deductions",
		size: 210,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "netAmount",
		header: "Net Pay",
		size: 210,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
];

// ==================== SSF REPORT COLUMNS ====================
export const ssfReportColumns: ColumnDef<any>[] = [
	{
		accessorKey: "id",
		header: "Employee Code",
		size: 200,
		minSize: 150,
		maxSize: 300,
	},
	{
		accessorKey: "lastName",
		header: "Last Name",
		size: 220,
		minSize: 180,
		maxSize: 350,
	},
	{
		accessorKey: "firstName",
		header: "First Name",
		size: 220,
		minSize: 180,
		maxSize: 350,
	},
	{
		accessorKey: "otherNames",
		header: "Other Names",
		size: 220,
		minSize: 180,
		maxSize: 350,
	},
	{
		accessorKey: "fullName",
		header: "Full Name",
		size: 220,
		minSize: 180,
		maxSize: 350,
	},
	{
		accessorKey: "email",
		header: "Email Address",
		size: 220,
		minSize: 180,
		maxSize: 350,
	},
	{
		accessorKey: "msisdn",
		header: "Contact Number",
		size: 220,
		minSize: 180,
		maxSize: 300,
	},
	{
		accessorKey: "ssn",
		header: "SSN",
		size: 220,
		minSize: 180,
		maxSize: 300,
	},
	{
		accessorKey: "basicSalary",
		header: "Basic Salary",
		size: 210,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "employeeContribution",
		header: "Employee Contribution",
		size: 250,
		minSize: 200,
		maxSize: 350,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "employerContribution",
		header: "Employer Contribution",
		size: 250,
		minSize: 200,
		maxSize: 350,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "total",
		header: "Contribution Total",
		size: 250,
		minSize: 200,
		maxSize: 350,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "tierAmount",
		header: "Tier Amount",
		size: 210,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "nationalIdNumber",
		header: "National ID Number",
		size: 220,
		minSize: 180,
		maxSize: 300,
	},
];

// ==================== SAVING SCHEME COLUMNS ====================
export const savingSchemeColumns: ColumnDef<any>[] = [
	{
		accessorKey: "id",
		header: "Employee Code",
		size: 200,
		minSize: 150,
		maxSize: 300,
	},
	{
		accessorKey: "lastName",
		header: "Surname",
		size: 220,
		minSize: 180,
		maxSize: 350,
	},
	{
		accessorKey: "firstName",
		header: "First Name",
		size: 220,
		minSize: 180,
		maxSize: 350,
	},
	{
		accessorKey: "birthDate",
		header: "Date of Birth",
		size: 240,
		minSize: 180,
		maxSize: 350,
	},
	{
		accessorKey: "employmentDate",
		header: "Date Joined Company",
		size: 240,
		minSize: 180,
		maxSize: 350,
	},
	{
		accessorKey: "ssn",
		header: "Social Security Number",
		size: 220,
		minSize: 180,
		maxSize: 300,
	},
	{
		accessorKey: "basicSalary",
		header: "Basic Salary",
		size: 220,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "employeeContribution",
		header: "Employee Contribution",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "employerContribution",
		header: "Employer Contribution",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "msisdn",
		header: "Phone Number",
		size: 220,
		minSize: 180,
		maxSize: 300,
	},
	{
		accessorKey: "gender",
		header: "Sex",
		size: 220,
		minSize: 150,
		maxSize: 300,
	},
];

// ==================== BANK REPORT COLUMNS ====================
export const bankReportColumns: ColumnDef<any>[] = [
	{
		accessorKey: "EMPLOYEE_CODE",
		header: "Employee Code",
		size: 200,
		minSize: 150,
		maxSize: 300,
	},
	{
		accessorKey: "EMPLOYEE_NAME",
		header: "Employee Name",
		size: 220,
		minSize: 180,
		maxSize: 350,
	},
	{
		accessorKey: "BANK_CODE",
		header: "Bank Code",
		size: 200,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => (getValue() as string) || "--",
	},
	{
		accessorKey: "BANK_NAME",
		header: "Bank Name",
		size: 220,
		minSize: 180,
		maxSize: 350,
		cell: ({ getValue }) => (getValue() as string) || "--",
	},
	{
		accessorKey: "NET",
		header: "Net Amount",
		size: 220,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "ACCOUNT_NUMBER",
		header: "Account Number",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => (getValue() as string) || "--",
	},
	{
		accessorKey: "BANK_BRANCH_CODE",
		header: "Branch Code",
		size: 220,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => (getValue() as string) || "--",
	},
	{
		accessorKey: "BANK_BRANCH_NAME",
		header: "Branch Name",
		size: 220,
		minSize: 180,
		maxSize: 350,
		cell: ({ getValue }) => (getValue() as string) || "--",
	},
	{
		accessorKey: "PERIOD DATE",
		header: "Period Dates",
		size: 250,
		minSize: 200,
		maxSize: 350,
		cell: ({ row }) => {
			const value = row.original?.["PERIOD DATE"];
			if (!value) return "--";

			const date = new Date(value as string);
			if (isNaN(date.getTime())) return "--";

			return date.toLocaleDateString("en-GB", {
				day: "2-digit",
				month: "2-digit",
				year: "2-digit",
			});
		},
	},
	{
		accessorKey: "REMARKS",
		header: "Remark",
		size: 250,
		minSize: 200,
		maxSize: 400,
		cell: ({ getValue }) => (getValue() as string) || "--",
	},
];

// ==================== TAX REPORT COLUMNS ====================
export const taxReportColumns: ColumnDef<any>[] = [
	{
		accessorKey: "employeeTaxNumber",
		header: "TIN/NATIONAL ID",
		size: 200,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => (getValue() as string) || "--",
	},
	{
		accessorKey: "employeeName",
		header: "Employee Name",
		size: 220,
		minSize: 180,
		maxSize: 350,
	},
	{
		accessorKey: "position",
		header: "Position",
		size: 180,
		minSize: 150,
		maxSize: 300,
	},
	{
		accessorKey: "residencyType",
		header: "Residency Type",
		size: 180,
		minSize: 150,
		maxSize: 300,
	},
	{
		accessorKey: "basicSalaryAmount",
		header: "Basic Salary",
		size: 180,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "isSecondaryEmployment",
		header: "Secondary Employment",
		size: 200,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => (getValue() ? "Yes" : "No"),
	},
	{
		accessorKey: "paidSSNIT",
		header: "Paid SSNIT",
		size: 160,
		minSize: 120,
		maxSize: 250,
		cell: ({ getValue }) => (getValue() ? "Yes" : "No"),
	},
	{
		accessorKey: "socialSecurityAmount",
		header: "SSNIT Amount",
		size: 180,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "thirdTier",
		header: "3rd Tier",
		size: 140,
		minSize: 120,
		maxSize: 250,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "allowanceAmount",
		header: "Allowance",
		size: 160,
		minSize: 130,
		maxSize: 250,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "bonusAmount",
		header: "Bonus",
		size: 160,
		minSize: 130,
		maxSize: 250,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "bonusTaxAmount",
		header: "Bonus Tax",
		size: 160,
		minSize: 130,
		maxSize: 250,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "excessBonusAmount",
		header: "Excess Bonus",
		size: 160,
		minSize: 130,
		maxSize: 250,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "totalCashEmolument",
		header: "Cash Emolument",
		size: 200,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "benefitInKindAmount",
		header: "Benefit In Kind",
		size: 200,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "totalAssessableIncomeAmount",
		header: "Assessable Income",
		size: 220,
		minSize: 180,
		maxSize: 320,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "deductibleReliefAmount",
		header: "Deductible Relief",
		size: 160,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "totalReliefAmount",
		header: "Total Relief",
		size: 160,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "chargeableIncomeAmount",
		header: "Chargeable Income",
		size: 200,
		minSize: 180,
		maxSize: 320,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "taxDeductibleAmount",
		header: "Tax Deductible",
		size: 200,
		minSize: 180,
		maxSize: 320,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "overtimeIncomeAmount",
		header: "Overtime Income",
		size: 200,
		minSize: 180,
		maxSize: 320,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "overtimeTaxAmount",
		header: "Overtime Tax",
		size: 200,
		minSize: 180,
		maxSize: 320,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "totalTaxPayableAmount",
		header: "Total Tax Payable",
		size: 220,
		minSize: 180,
		maxSize: 350,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
];

// ==================== PAYROLL SUMMARY COLUMNS ====================
export const payrollSummaryColumns: ColumnDef<any>[] = [
	{
		accessorKey: "particulars",
		header: ({ column }) => {
			return (
				<div className="flex items-center gap-2 font-semibold text-gray-700">
					Particulars
				</div>
			);
		},
		cell: ({ getValue }) => (
			<div className="font-medium text-gray-900 py-2">
				{getValue() as string}
			</div>
		),
		size: 300,
		minSize: 250,
		maxSize: 500,
	},
	{
		accessorKey: "debit",
		header: ({ column }) => {
			return (
				<div className="flex items-center justify-end gap-2 font-semibold text-gray-700">
					Debit
				</div>
			);
		},
		cell: ({ getValue }) => {
			const value = getValue();
			return (
				<div className="text-right font-medium text-gray-900 py-2 pr-4">
					{value !== null && value !== undefined
						? moneyFormat(value as number)
						: "--"}
				</div>
			);
		},
		size: 180,
		minSize: 150,
		maxSize: 300,
	},
	{
		accessorKey: "credit",
		header: ({ column }) => {
			return (
				<div className="flex items-center justify-center gap-2 font-semibold text-gray-700">
					Credit
				</div>
			);
		},
		cell: ({ getValue }) => {
			const value = getValue();
			return (
				<div className="text-center font-medium text-gray-900 py-2 pr-4">
					{value !== null && value !== undefined
						? moneyFormat(value as number)
						: "--"}
				</div>
			);
		},
		size: 400,
		minSize: 150,
		maxSize: 400,
	},
];

// ==================== SALARY ADVANCE REPORT COLUMNS ====================
export const salaryAdvanceReportColumns: ColumnDef<any>[] = [
	{
		accessorKey: "employeeCode",
		header: "Employee Code",
		size: 150,
		minSize: 120,
		maxSize: 250,
		cell: ({ row }) =>
			row.original.isStationHeader ? "" : row.original.employeeCode,
	},
	{
		accessorKey: "employeeName",
		header: "Employee Name",
		size: 200,
		minSize: 180,
		maxSize: 350,
		cell: ({ row }) =>
			row.original.isStationHeader
				? row.original.stationName
				: row.original.employeeName,
	},
	{
		accessorKey: "advanceAmount",
		header: "Advance Amount",
		size: 150,
		minSize: 120,
		maxSize: 250,
		cell: ({ row }) => {
			if (row.original.isStationHeader) return "";
			const value = row.original.advanceAmount;
			return value !== null ? moneyFormat(value) : "--";
		},
	},
];

export const payeSummaryColumns: ColumnDef<any>[] = [
	{
		accessorKey: "categoryName",
		header: "Category",
		size: 350,
		minSize: 250,
		maxSize: 500,
	},
	{
		accessorKey: "numberOfStaff",
		header: "Number of Staff",
		size: 200,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) =>
			new Intl.NumberFormat().format(getValue() as number),
	},
	{
		accessorKey: "totalEmolument",
		header: "Total Emolument",
		size: 220,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "taxDeducted",
		header: "Tax Deducted",
		size: 220,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
];

export const employeeAnnualTaxDeductionColumns: ColumnDef<any>[] = [
	{
		accessorKey: "month",
		header: "Month",
		size: 80,
	},
	{
		accessorKey: "consolidatedBasicSalary",
		header: "Basic Salary",
		size: 150,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "cashAllowanceNotConsolidated",
		header: "Cash Allowance",
		size: 160,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "benefitInKind",
		header: "Benefit In Kind",
		size: 150,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "grossRemuneration",
		header: "Gross Remuneration",
		size: 180,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "deductibleRelief",
		header: "Deductible Relief",
		size: 160,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "socialSecurityRelief",
		header: "SSF Relief",
		size: 130,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "taxableEmolument",
		header: "Taxable Emolument",
		size: 175,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "taxDeducted",
		header: "Tax Deducted",
		size: 140,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
];
